"use client";

import React from "react";
import Image from "next/image";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { GROQImage } from "@/sanity/lib/definitions";
import { ChangelogSceneKey, isChangelogSceneKey } from "./scenes/sceneConfig";
import ChangelogScene from "./ChangelogScene";

const EASE = [0.22, 1, 0.36, 1] as const;
const REVEAL_IMAGE = {
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.25 },
  transition: { duration: 0.7, ease: [...EASE] },
} as const;

function EntryImage({ image, alt, className }: { image: GROQImage; alt: string; className?: string }) {
  return (
    <motion.div
      {...REVEAL_IMAGE}
      className={cn("relative w-full overflow-hidden", className)}>
      <Image
        src={image}
        alt={alt}
        quality={100}
        sizes="(min-width: 993px) 88rem, 100vw"
        placeholder={image.lqip ? "blur" : "empty"}
        blurDataURL={image.lqip}
        className="block h-auto w-full"
      />
    </motion.div>
  );
}

function EntryRaster({ image, imageMobile, alt }: { image?: GROQImage; imageMobile?: GROQImage; alt: string }) {
  return (
    <>
      {image && (
        <EntryImage
          image={image}
          alt={alt}
          className={cn(imageMobile && "hidden lg:block")}
        />
      )}
      {imageMobile && (
        <EntryImage
          image={imageMobile}
          alt={alt}
          className="lg:hidden"
        />
      )}
    </>
  );
}

export default function ChangelogEntryImage({
  image,
  imageMobile,
  alt,
  scene,
}: {
  image?: GROQImage;
  imageMobile?: GROQImage;
  alt: string;
  /** Animated scene; replaces the raster at every breakpoint, which stays as the fallback. */
  scene?: ChangelogSceneKey;
}) {
  const raster = (
    <EntryRaster
      image={image}
      imageMobile={imageMobile}
      alt={alt}
    />
  );

  // The scene runs its own reveal, so it is not wrapped in the framer fade.
  if (isChangelogSceneKey(scene)) {
    return (
      <ChangelogScene
        scene={scene}
        label={alt}
        fallback={raster}
      />
    );
  }

  return raster;
}
